import { useCallback, useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import DataTable from "@/components/DataTable";
import { ScadenzaEditor, RinnovaDialog } from "@/components/ScadenzaForms";
import { statusMeta, giorniLabel, STATUS_META } from "@/lib/scadenze";
import { formatDate, formatCurrency } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const WRITE = { veicolo: ["admin", "traffico", "officina"], autista: ["admin", "traffico"] };
const BUCKETS = ["rosso", "arancione", "giallo", "verde"];

export default function Scadenzario({ entityType }) {
  const { user } = useAuth();
  const canWrite = user?.role === "admin" || WRITE[entityType]?.includes(user?.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [filters, setFilters] = useState({});
  const [tipi, setTipi] = useState([]);
  const [editing, setEditing] = useState(null);
  const [rinnova, setRinnova] = useState(null);

  useEffect(() => {
    api.get(`/scadenze/tipi`, { params: { entity_type: entityType } }).then((r) => setTipi(r.data));
    setFilters({});
    setSearch("");
  }, [entityType]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(() => {
    setLoading(true);
    const params = { entity_type: entityType };
    if (debounced) params.search = debounced;
    if (filters.status) params.status = filters.status;
    if (filters.tipo) params.tipo = filters.tipo;
    api
      .get(`/scadenze`, { params })
      .then((r) => setItems(r.data.items))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [entityType, debounced, filters]);

  useEffect(() => {
    load();
  }, [load]);

  const buckets = useMemo(() => {
    const b = { rosso: 0, arancione: 0, giallo: 0, verde: 0 };
    items.forEach((s) => s.status && (b[s.status] += 1));
    return b;
  }, [items]);

  const del = async (id) => {
    try {
      await api.delete(`/scadenze/${id}`);
      toast.success("Scadenza eliminata");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const doExport = async () => {
    try {
      const params = { entity_type: entityType };
      if (debounced) params.search = debounced;
      if (filters.status) params.status = filters.status;
      if (filters.tipo) params.tipo = filters.tipo;
      const res = await api.get(`/scadenze/export`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `scadenze_${entityType}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const columns = [
    { key: "parent_label", label: entityType === "veicolo" ? "Veicolo" : "Autista", render: (r) => <span className="font-medium text-slate-800">{r.parent_label}</span> },
    { key: "tipo", label: "Tipo", render: (r) => (<div><div>{r.tipo}</div>{r.descrizione && <div className="text-xs text-slate-400">{r.descrizione}</div>}</div>) },
    { key: "data_scadenza", label: "Scadenza", nowrap: true, render: (r) => (<span>{formatDate(r.data_scadenza)}<span className="text-xs text-slate-400 ml-2">{giorniLabel(r.giorni)}</span></span>) },
    { key: "status", label: "Stato", sortable: false, render: (r) => { const sm = statusMeta(r.status); return <span className={`scad-badge ${sm.cls}`}>{sm.label}</span>; } },
    { key: "costo_ultimo_rinnovo", label: "Ultimo costo", align: "right", render: (r) => (r.costo_ultimo_rinnovo != null ? formatCurrency(r.costo_ultimo_rinnovo) : "—") },
  ];

  const filterFields = [
    { name: "status", label: "Stato", options: BUCKETS.map((b) => ({ value: b, label: STATUS_META[b].label })) },
    { name: "tipo", label: "Tipo", options: tipi.map((t) => ({ value: t, label: t })) },
  ];

  return (
    <div className="space-y-4" data-testid={`scadenzario-${entityType}`}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {BUCKETS.map((b) => (
          <div key={b} className={`rounded-md p-3 border ${statusMeta(b).cls}`}>
            <div className="text-[11px] font-semibold uppercase tracking-wider opacity-80">
              {statusMeta(b).label}
            </div>
            <div className="text-2xl font-head font-bold tabular-nums">{buckets[b]}</div>
          </div>
        ))}
      </div>

      <DataTable
        testid={`scad-table-${entityType}`}
        columns={columns}
        rows={items}
        loading={loading}
        total={items.length}
        pageSize={items.length || 1}
        page={1}
        sortBy={null}
        onSort={null}
        search={search}
        onSearch={setSearch}
        filterFields={filterFields}
        filterValues={filters}
        onFilter={(name, val) => setFilters((p) => ({ ...p, [name]: val }))}
        onExport={doExport}
        canWrite={false}
        emptyLabel="Nessuna scadenza"
        renderActions={
          canWrite
            ? (row) => (
                <div className="flex items-center justify-end gap-1">
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-slate-400 hover:text-green-700" onClick={() => setRinnova(row)} title="Rinnova" data-testid={`scad-rinnova-${row.id}`}>
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-slate-400 hover:text-sky-700" onClick={() => setEditing(row)} data-testid={`scad-edit-${row.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-slate-400 hover:text-red-600" onClick={() => del(row.id)} data-testid={`scad-del-${row.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )
            : undefined
        }
      />

      {editing && (
        <ScadenzaEditor
          open={!!editing}
          onOpenChange={(v) => !v && setEditing(null)}
          entityType={entityType}
          entityId={editing.entity_id}
          initial={editing}
          onSaved={load}
        />
      )}
      {rinnova && (
        <RinnovaDialog
          open={!!rinnova}
          onOpenChange={(v) => !v && setRinnova(null)}
          scadenza={rinnova}
          onSaved={load}
        />
      )}
    </div>
  );
}
